import chalk from 'chalk';
import {
  checkHookSync,
  pushToHookSync,
  popFromHookSync,
  clearHookSync,
  sendMailSync,
  generateFixedPointPromptSync,
  HookItem,
} from '../../../lib/hooks.js';
import { normalizeAgentId } from '../../../lib/agents.js';

interface HookOptions {
  json?: boolean;
}

export async function hookCommand(
  action: string,
  idOrMessage?: string,
  options: HookOptions = {}
): Promise<void> {
  // Normalize agent ID
  const agentId = process.env.OVERDECK_AGENT_ID || 'default';

  switch (action) {
    case 'check': {
      const result = checkHookSync(idOrMessage || agentId);

      if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }

      if (!result.hasWork) {
        console.log(chalk.green('✓ No pending work on hook'));
        return;
      }

      console.log(chalk.yellow(`⚠ ${result.items.length} item(s) on hook`));
      if (result.urgentCount > 0) {
        console.log(chalk.red(`  ${result.urgentCount} URGENT`));
      }
      console.log('');

      for (const item of result.items) {
        const priorityColor = {
          urgent: chalk.red,
          high: chalk.yellow,
          normal: chalk.white,
          low: chalk.dim,
        }[item.priority];

        console.log(`${priorityColor(`[${item.priority.toUpperCase()}]`)} ${item.id}`);
        console.log(`  Type: ${item.type}`);
        console.log(`  From: ${item.source}`);
        if (item.payload.message) {
          console.log(`  Message: ${item.payload.message}`);
        }
        console.log('');
      }
      break;
    }

    case 'push': {
      if (!idOrMessage) {
        console.log(chalk.red('Usage: pan admin fpp push <agent-id> <message>'));
        process.exit(1);
      }

      const [targetAgent, ...messageParts] = idOrMessage.split(' ');
      const message = messageParts.join(' ');

      if (!message) {
        console.log(chalk.red('Message required'));
        process.exit(1);
      }

      const item = pushToHookSync(normalizeAgentId(targetAgent), {
        type: 'task',
        priority: 'normal',
        source: 'cli',
        payload: { message },
      });

      console.log(chalk.green(`✓ Pushed to hook: ${item.id}`));
      break;
    }

    case 'pop': {
      if (!idOrMessage) {
        console.log(chalk.red('Usage: pan admin fpp pop <item-id>'));
        process.exit(1);
      }

      const success = popFromHookSync(agentId, idOrMessage);
      if (success) {
        console.log(chalk.green(`✓ Popped: ${idOrMessage}`));
      } else {
        console.log(chalk.yellow(`Item not found: ${idOrMessage}`));
      }
      break;
    }

    case 'clear': {
      clearHookSync(idOrMessage || agentId);
      console.log(chalk.green('✓ Hook cleared'));
      break;
    }

    case 'mail': {
      if (!idOrMessage) {
        console.log(chalk.red('Usage: pan admin fpp mail <agent-id> <message>'));
        process.exit(1);
      }

      const [targetAgent, ...messageParts] = idOrMessage.split(' ');
      const message = messageParts.join(' ');

      if (!message) {
        console.log(chalk.red('Message required'));
        process.exit(1);
      }

      sendMailSync(
        normalizeAgentId(targetAgent),
        'cli',
        message
      );

      console.log(chalk.green(`✓ Mail sent to ${targetAgent}`));
      break;
    }

    case 'fpp': {
      const prompt = generateFixedPointPromptSync(idOrMessage || agentId);

      if (!prompt) {
        console.log(chalk.green('No fixed point work found'));
        return;
      }

      console.log(prompt);
      break;
    }

    default:
      console.log(chalk.bold('Hook Commands:'));
      console.log('');
      console.log(`  ${chalk.cyan('pan admin fpp check [agent-id]')}  - Check for pending work`);
      console.log(`  ${chalk.cyan('pan admin fpp push <agent-id> <msg>')}  - Push task to hook`);
      console.log(`  ${chalk.cyan('pan admin fpp pop <item-id>')}  - Remove completed item`);
      console.log(`  ${chalk.cyan('pan admin fpp clear [agent-id]')}  - Clear all hook items`);
      console.log(`  ${chalk.cyan('pan admin fpp mail <agent-id> <msg>')}  - Send mail to agent`);
      console.log(`  ${chalk.cyan('pan admin fpp fpp [agent-id]')}  - Generate FPP prompt`);
  }
}
